import Icon from '@material-ui/core/Icon'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import { MUIDataTableColumnDef } from 'mui-datatables'
import React, { Fragment, useState } from 'react'
import { EnumUser } from '../../../../../graphql/typings/global_types'
import DataTable from '../../../../components/DataTable'
import { ConfirmDialog } from '../../../../components/Dialog'
import Panel from '../../../../components/Panel'
import { GuaranteeCategory } from '../../../../helpers/data/models/contract/GuaranteeCategory'
import { useErrorService } from '../../../../helpers/errors/ErrorService'
import { useAuthUser } from '../../../../hooks/useAuth'
import { useCommonDataLoadedExisting } from '../../../../hooks/useCommonData'
import { useDeleteGuaranteeCategory } from '../../../../hooks/useMutations'
import useSnackBar from '../../../../hooks/useSnackBar'
import GuaranteeCategoryDialog from './GuaranteeCategoryDialog'

type DialogState =
    | {
          open: true
          guaranteeCategory: GuaranteeCategory
      }
    | { open: false }

type FormattedCategory = {
    id: string
    title: string
    description: string
}

const AEAGuaranteeCategoriesList = () => {
    const { guaranteeCategories } = useCommonDataLoadedExisting()
    const { errorAlert } = useErrorService()
    const auth = useAuthUser()
    const isAdmin = auth.user.userType === EnumUser.admin
    const { openSnack } = useSnackBar()
    const deleteGuaranteeCategory = useDeleteGuaranteeCategory()

    const [openAddDialog, setOpenAddDialog] = useState<boolean>(false)
    const [openEditDialog, setOpenEditDialog] = useState<DialogState>({ open: false })
    const [openDeleteDialog, setOpenDeleteDialog] = useState<DialogState>({
        open: false,
    })
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false)

    const columns: MUIDataTableColumnDef[] = [
        {
            name: 'title',
            label: 'Nom',
        },
        {
            name: 'description',
            label: 'Description',
        },
    ]

    const addFeatureData = {
        addHandler: () => {
            setOpenAddDialog(true)
        },
        tooltip: `Ajout d'une catégorie de garantie`,
    }

    const onCreated = (guaranteeCategory: GuaranteeCategory) => {
        openSnack({
            type: 'success',
            message: `Catégorie ${guaranteeCategory.title} créée avec succès !`,
        })
    }

    const onEdited = (guaranteeCategory: GuaranteeCategory) => {
        openSnack({
            message: 'Catégorie modifiée avec succès !',
            type: 'success',
        })
    }

    const onEditClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        guaranteeCategory: GuaranteeCategory
    ) => {
        event.stopPropagation()
        setOpenEditDialog({ open: true, guaranteeCategory })
    }

    const onDeleteClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        guaranteeCategory: GuaranteeCategory
    ) => {
        event.stopPropagation()
        setOpenDeleteDialog({ open: true, guaranteeCategory })
    }

    const onDeleteConfirmed = async () => {
        if (!openDeleteDialog.open) return

        setDeleteLoading(true)

        try {
            const guaranteeCategory = openDeleteDialog.guaranteeCategory

            const response = await deleteGuaranteeCategory.run({
                id: guaranteeCategory.id,
            })

            if (response.errors.length > 0) {
                throw 'Une erreur est survenue pendant la suppression de la catégorie.'
            } else {
                openSnack({
                    message: 'Catégorie supprimée avec succès !',
                    type: 'success',
                })
            }
        } catch (error) {
            errorAlert(error)
        } finally {
            setDeleteLoading(false)
            setOpenDeleteDialog({ open: false })
        }
    }

    const actionsColumn = (dataIndex: number, rowIndex: number) => {
        if (!isAdmin) return null

        const guaranteeCategory = guaranteeCategories[dataIndex]

        return (
            <div className="flex items-center">
                <div className="flex-grow"></div>
                <Tooltip title="Modifier la catégorie">
                    <IconButton onClick={(event) => onEditClick(event, guaranteeCategory)}>
                        <Icon>edit</Icon>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Supprimer la catégorie">
                    <IconButton onClick={(event) => onDeleteClick(event, guaranteeCategory)}>
                        <Icon>delete</Icon>
                    </IconButton>
                </Tooltip>
            </div>
        )
    }

    const formattedCategories: FormattedCategory[] = guaranteeCategories.map((category) => {
        return {
            ...category,
            description: category.description || '-',
        }
    })

    return (
        <Fragment>
            <Panel>
                <DataTable<FormattedCategory>
                    title={'Catégories de garanties'}
                    data={formattedCategories}
                    columns={columns}
                    showSearch={true}
                    addFeature={addFeatureData}
                    actionsColumn={actionsColumn}
                    options={{
                        sortOrder: {
                            name: 'title',
                            direction: 'asc',
                        },
                    }}
                />
            </Panel>
            <GuaranteeCategoryDialog
                onDone={onCreated}
                open={openAddDialog}
                closeDialog={() => setOpenAddDialog(false)}
            />
            {openEditDialog.open && (
                <GuaranteeCategoryDialog
                    onDone={onEdited}
                    open={openEditDialog.open}
                    closeDialog={() => setOpenEditDialog({ open: false })}
                    guaranteeCategory={openEditDialog.guaranteeCategory}
                />
            )}
            {openDeleteDialog.open && (
                <ConfirmDialog
                    open={openDeleteDialog.open}
                    onCancelled={() => setOpenDeleteDialog({ open: false })}
                    onConfirmed={onDeleteConfirmed}
                    title="Suppression d'une catégorie de garantie"
                    text={`Voulez-vous vraiment supprimer la catégorie "${openDeleteDialog.guaranteeCategory.title}" ? Toutes les garanties associés à cette catégorie seront supprimées de leurs contrats (rétroactivement) !`}
                    loading={deleteLoading}
                />
            )}
        </Fragment>
    )
}

export default AEAGuaranteeCategoriesList
